import React, { useState, useEffect } from 'react';
import { Plus, Download, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { AddTableModal } from '../components/accommodation/AddTableModal';
import { AccommodationTable } from '../components/accommodation/AccommodationTable';
import { useAccommodationStore } from '../stores/accommodationStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';
import { usePilgrimGuideStore } from '../stores/pilgrimGuideStore';
import { usePackagesStore } from '../stores/packagesStore';
import { exportAllAccommodationToExcel } from '../utils/exportAccommodation';
import toast from 'react-hot-toast';
import { DateConversionModal } from '../components/accommodation/DateConversionModal';

export const AccommodationPage = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDateConversionModalOpen, setIsDateConversionModalOpen] = useState(false);
  const { tables, loading, error, fetchTables } = useAccommodationStore();
  const { translations } = useLanguage();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const isEmployee = userRole === 'admin';
  const { initialized: linksInitialized, fetchLinks } = usePilgrimGuideStore();
  const { packages, fetchPackages, loading: loadingPackages } = usePackagesStore();
  const [filteredTables, setFilteredTables] = useState<Record<string, any>>({});
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Fetch initial data only once
  useEffect(() => {
    // Prevent multiple fetches
    if (initialDataLoaded) return;

    const loadInitialData = async () => {
      try {
        // Fetch tables data
        await fetchTables();
        
        // Fetch packages data
        await fetchPackages();
        
        // If organizer and links not initialized, fetch links
        if (userRole === 'organizer' && !linksInitialized) {
          await fetchLinks();
        }
        
        setInitialDataLoaded(true);
      } catch (error) {
        console.error("Error loading initial data:", error);
      }
    };
    
    loadInitialData();
  }, [fetchTables, fetchPackages, fetchLinks, userRole, linksInitialized, initialDataLoaded]);

  // Filter tables only when packages and links data has loaded
  useEffect(() => {
    // Skip if still loading or no tables
    if (!initialDataLoaded || !Object.keys(tables).length) return;

    if (userRole === 'organizer' && userId) {
      // For organizers, filter tables by the hotels in their packages
      import('../stores/pilgrimGuideStore').then(module => {
        const linkStore = module.usePilgrimGuideStore.getState();
        
        // Get organizer's pilgrims
        const organizerPilgrims = Object.values(linkStore.links).filter(link =>
          link.organizerId === userId && (link.status === 'approved' || link.status === 'pending')
        );
        
        // Create a set of eligible hotel names
        const eligibleHotels = new Set<string>();
        
        organizerPilgrims.forEach(pilgrim => {
          // Find the pilgrim's package
          const pilgrimPackage = Object.values(packages).find(pkg => pkg.name === pilgrim.packageName);
          if (pilgrimPackage) {
            if (pilgrimPackage.mainMakkahHotel) eligibleHotels.add(pilgrimPackage.mainMakkahHotel);
            if (pilgrimPackage.shiftingMakkahHotel) eligibleHotels.add(pilgrimPackage.shiftingMakkahHotel);
            if (pilgrimPackage.madinahHotel) eligibleHotels.add(pilgrimPackage.madinahHotel);
          }
        });
        
        // Filter tables to only include those with eligible hotels
        const filtered = Object.entries(tables).reduce((acc, [id, table]) => {
          if (eligibleHotels.has(table.hn)) {
            acc[id] = table;
          }
          return acc;
        }, {} as Record<string, any>);
        
        setFilteredTables(filtered);
      });
    } else {
      // For admin and guides, show all tables
      setFilteredTables(tables);
    }
  }, [initialDataLoaded, tables, packages, userRole, userId]);

  // Export all accommodation data from all hotels
  const handleExportAllAccommodation = async () => {
    try {
      setIsExporting(true);
      await exportAllAccommodationToExcel();
      toast.success(translations?.accommodation?.exportSuccess || 'All accommodation data exported successfully');
    } catch (error: any) {
      console.error('Error exporting all accommodation data:', error);
      toast.error(error.message || translations?.accommodation?.exportError || 'Failed to export accommodation data');
    } finally {
      setIsExporting(false);
    }
  };

  // Show loading spinner while initial data is loading
  if (!initialDataLoaded) {
    return <LoadingSpinner />;
  }
  
  if (error) return <ErrorMessage message={error} />;

  const tablesToDisplay = userRole === 'organizer' ? filteredTables : tables;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4 md:space-y-6"
    >
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">
            {translations?.accommodation?.header || 'Hotel Accommodation'}
          </h1>
          <p className="text-sm md:text-base text-gray-500 mt-1">
            {translations?.accommodation?.description || "Manage pilgrims' accommodation in hotels"}
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          
          
          {/* Export All Hotels Button - Only for admin */}
          {isEmployee && (
            <button
              onClick={handleExportAllAccommodation}
              disabled={isExporting || Object.keys(tables).length === 0}
              className="w-full sm:w-auto bg-blue-500 text-white px-4 py-2.5 rounded-lg hover:bg-blue-600 
                       transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Download size={20} />
              )}
              <span className="whitespace-nowrap">
                {isExporting
                  ? (translations?.accommodation?.exporting || 'Exporting...')
                  : (translations?.accommodation?.exportAll || 'Export All Hotels')}
              </span>
            </button>
          )}

          {isEmployee && (
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="w-full sm:w-auto bg-primary-500 text-white px-4 py-2.5 rounded-lg hover:bg-primary-600 
                       transition-colors flex items-center justify-center gap-2 shadow-sm hover:shadow"
            >
              <Plus size={20} />
              <span className="whitespace-nowrap">
                {translations?.accommodation?.addButton || 'Add Accommodation Table'}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* Accommodation Tables */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
        {Object.values(tablesToDisplay).map((table) => (
          <motion.div
            key={table.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <AccommodationTable table={table} />
          </motion.div>
        ))}
      </div>

      {/* No Tables Message */}
      {Object.keys(tablesToDisplay).length === 0 && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-8 md:py-12 bg-white rounded-lg shadow-sm"
        >
          <p className="text-gray-500 px-4">
            {userRole === 'organizer' 
              ? (translations?.accommodation?.noTablesOrganizer || 'No accommodation tables available for your pilgrims\' packages.') 
              : (translations?.accommodation?.noTables || 'No accommodation tables available currently.')}
          </p>
        </motion.div>
      )}

      {/* Add Table Modal */}
      <AddTableModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
      
      {/* Date Conversion Modal */}
      <DateConversionModal 
        isOpen={isDateConversionModalOpen}
        onClose={() => setIsDateConversionModalOpen(false)}
      />
    </motion.div>
  );
};